import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import Button from '@components/common/Button';
import { Input, Select, Textarea } from '@components/common';
import EvidenceUploadZone from './EvidenceUploadZone';
import { useAuth } from '@contexts/AuthContext';
import { evidenceService } from '@services';
import { AssessmentItemsResponse, EVIDENCE_TYPE_OPTIONS } from '@app-types';
import type { EvidenceRecordStatus, EvidenceType } from '@app-types';
import { ApiError, ROLES } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import styles from './EvidenceUploadForm.module.css';

interface EvidenceFormData {
  evidence_type: EvidenceType;
  evidence_name: string;
  evidence_description: string;
  notes: string;
}

const getBackLabel = (returnTo: string): string => {
  if (returnTo.startsWith('/evidence')) return 'Back to Evidence Management';
  if (returnTo === '/assessments') return 'Back to Assessments';
  return 'Back to Assessment';
};

const EvidenceUploadForm: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Entry points pass returnTo. Without it, Contract Role goes to Evidence Management and an
  // Assessor, who cannot open that page, to their assessments.
  const defaultReturnTo = hasRole(user, ROLES.CONTRACT_ROLE) ? '/evidence' : '/assessments';
  const returnTo = (location.state as { returnTo?: string })?.returnTo || defaultReturnTo;

  const [formData, setFormData] = useState<EvidenceFormData>({
    evidence_type: 'document',
    evidence_name: '',
    evidence_description: '',
    notes: ''
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle file selection from EvidenceUploadZone
  const handleFilesSelect = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
    // Clear file errors when new valid files are selected
    if (errors.files) {
      const newErrors = { ...errors };
      delete newErrors.files;
      setErrors(newErrors);
    }
  };

  // Handle validation errors from EvidenceUploadZone
  const handleValidationError = (error: string) => {
    setErrors((prev) => ({ ...prev, files: error }));
  };

  // Remove file from selection
  const handleFileRemove = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload evidence mutation
  const uploadEvidenceMutation = useMutation({
    mutationFn: (data: EvidenceFormData) => {
      if (!itemId) throw new Error('Item ID is required');
      return evidenceService.uploadEvidence({
        assessment_item_id: Number.parseInt(itemId, 10),
        ...data,
        files: selectedFiles
      });
    },
    onSuccess: (result) => {
      const uploadedItemId = Number.parseInt(itemId ?? '', 10);
      // The backend decides: the assigned Assessor's upload is a draft, a CM/CO upload is pending.
      const uploadedStatus: EvidenceRecordStatus = result?.evidence?.status ?? 'pending';
      const isDraft = uploadedStatus === 'draft';

      queryClient.setQueriesData<AssessmentItemsResponse>({ queryKey: ['assessment-items'] }, (old) => {
        if (!old?.items || !Number.isInteger(uploadedItemId)) {
          return old;
        }
        return {
          ...old,
          items: old.items.map((item) =>
            item.id === uploadedItemId
              ? {
                  ...item,
                  evidence_status: isDraft ? 'draft' : 'pending',
                  draft_count: (item.draft_count ?? 0) + (isDraft ? 1 : 0),
                  pending_count: (item.pending_count ?? 0) + (isDraft ? 0 : 1)
                }
              : item
          )
        };
      });

      void queryClient.invalidateQueries({ queryKey: ['contractManagerGapItems'] });
      if (Number.isInteger(uploadedItemId)) {
        void queryClient.invalidateQueries({ queryKey: ['assessment-item-evidence', uploadedItemId] });
      }

      navigate(returnTo, {
        replace: true,
        preventScrollReset: true,
        state: { scrollToItemId: uploadedItemId }
      });
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      console.error('Evidence upload error:', error);
      const errorMessage = apiError.response?.data?.error || apiError.message || 'Failed to upload evidence';
      setErrors({ submit: errorMessage });
    }
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.evidence_name.trim()) {
      newErrors.evidence_name = 'Evidence name is required';
    }

    if (!formData.evidence_description.trim()) {
      newErrors.evidence_description = 'Evidence description is required';
    }

    if (formData.evidence_description.trim().length < 10) {
      newErrors.evidence_description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    uploadEvidenceMutation.mutate(formData);
  };

  // An Assessor without Contract Role uploads on the CM's behalf, which the backend saves as a draft.
  const uploadsAsDraft = hasRole(user, ROLES.ASSESSOR) && !hasRole(user, ROLES.CONTRACT_ROLE);

  const handleInputChange = (field: keyof EvidenceFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // The backend narrows Assessors to the one assigned to the assessment (ADO-1148).
  if (!user || !hasRole(user, [ROLES.CONTRACT_ROLE, ROLES.ASSESSOR])) {
    return (
      <div className={styles.evidenceUploadContainer}>
        <div className={styles.uploadError}>
          <h2>Access Denied</h2>
          <p>You don't have permission to upload evidence.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.evidenceUploadContainer}>
      {/* Header */}
      <div className={styles.uploadHeader}>
        <Button variant="ghost" onClick={() => navigate(returnTo)} className={styles.backButton}>
          <ArrowLeft size={16} />
          {getBackLabel(returnTo)}
        </Button>

        <div className={styles.uploadTitle}>
          <h1>Upload Evidence</h1>
          <p>Provide evidence to address the identified assessment gap.</p>
          {uploadsAsDraft && (
            <p className={styles.draftNotice} data-test-id="evidence-upload-draft-notice">
              Your upload is saved as a draft. The Contract Manager reviews and submits it.
            </p>
          )}
        </div>
      </div>

      {/* Upload Form */}
      <div className={styles.uploadFormCard}>
        <div className={styles.formHeader}>
          <div className={styles.formIcon}>
            <Upload size={24} />
          </div>
          <div className={styles.formInfo}>
            <h2>Evidence Details</h2>
            <p>Fill in the details about the evidence you're providing.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.evidenceForm} data-test-id="evidence-upload-form">
          {/* Evidence Type */}
          <div className={styles.formGroup}>
            <Select
              id="evidence_type"
              label="Evidence Type"
              required
              value={formData.evidence_type}
              onChange={(value) => handleInputChange('evidence_type', value)}
              options={EVIDENCE_TYPE_OPTIONS.map((type) => ({
                value: type.value,
                label: `${type.label} - ${type.description}`
              }))}
              dataTestId="evidence-type"
            />
          </div>

          {/* Evidence Name */}
          <div className={styles.formGroup}>
            <Input
              id="evidence_name"
              label="Evidence Name"
              type="text"
              placeholder="e.g., Security Policy Document 2024"
              value={formData.evidence_name}
              onChange={(e) => handleInputChange('evidence_name', e.target.value)}
              required
              error={errors.evidence_name}
              dataTestId="evidence-name"
            />
          </div>

          {/* Evidence Description */}
          <div className={styles.formGroup}>
            <Textarea
              id="evidence_description"
              label="Evidence Description"
              required
              placeholder="Describe how this evidence addresses the identified gap..."
              rows={4}
              value={formData.evidence_description}
              onChange={(e) => handleInputChange('evidence_description', e.target.value)}
              error={errors.evidence_description}
              dataTestId="evidence-description"
            />
            <div className={styles.formHint}>
              Explain how this evidence demonstrates compliance or addresses the gap identified by the assessor.
            </div>
          </div>

          {/* Additional Notes */}
          <div className={styles.formGroup}>
            <Textarea
              id="notes"
              label="Additional Notes"
              placeholder="Any additional context or notes (optional)..."
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              dataTestId="notes"
            />
          </div>

          {/* File Upload Area */}
          <div className={styles.formGroup}>
            <span className={styles.formLabel}>Upload Files (Optional)</span>
            <EvidenceUploadZone
              selectedFiles={selectedFiles}
              onFilesSelect={handleFilesSelect}
              onFileRemove={handleFileRemove}
              onValidationError={handleValidationError}
              error={errors.files}
              isProcessing={uploadEvidenceMutation.isPending}
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className={clsx(styles.formError, styles.formErrorSubmit)}>
              <AlertTriangle size={16} />
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div className={styles.formActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(returnTo)}
              disabled={uploadEvidenceMutation.isPending}
              dataTestId="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={uploadEvidenceMutation.isPending}
              dataTestId="submit-button"
            >
              {uploadEvidenceMutation.isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Uploading Evidence...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload Evidence
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EvidenceUploadForm;
